import React, { ChangeEvent, Dispatch, useEffect, useRef, useState } from 'react'
import { UserDataRow } from '../earlyWarning/emailComp/EmailTypes'
import { UserType } from '../OrganizationAddUserComp/OrganizationAddUserTypes';

type UserSelectTablePropsType = {
    userSelect: UserDataRow[];
    setUserSelect: Dispatch<React.SetStateAction<UserDataRow[]>>;
    type: 'INTERNAL' | 'EXTERNAL' | 'ACK';
    token?: string;
}

const UserSelectTable = (props: UserSelectTablePropsType) => {
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const [selectedCodes, setSelectedCodes] = useState<Set<string>>(new Set());
    const rowNumber = useRef<HTMLInputElement | null>(null)
    const searchText = useRef<HTMLInputElement | null>(null)

    const [selectedPage, setSelectedPage] = useState<number>(0)
    const [sliceCount, setSliceCount] = useState<number>(10)

    const [slicedData, setSlicedData] = useState<UserDataRow[][]>([])

    const allUsers = useRef<UserDataRow[]>([]);
    const [filteredUsersData, setFilteredUserData] = useState<UserDataRow[]>([]);

    const [initialSelectedCodes, setInitialSelectedCodes] = useState<Set<string>>(new Set());
    const hasInitializedFromProps = useRef(false);

    const paginationList = (data: UserDataRow[], size: number): UserDataRow[][] => {
        const result: UserDataRow[][] = []

        if (size >= data.length) {
            return [data]
        }

        let start = 0

        while (start < data.length) {
            result.push(data.slice(start, start + size))
            start += size
        }

        return result
    }

    const paginationNavigation = (side: "next" | "prev") => {
        if (side === "next") {
            if (slicedData.length > selectedPage + 1) {
                setSelectedPage(prevState => prevState + 1)
            }

        } else if (side === "prev") {
            if (0 <= selectedPage - 1) {
                setSelectedPage(prevState => prevState - 1)
            }
        }
    }

    const handleSliceCountChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliceCount(event.target.valueAsNumber)
    }

    const handleSearchTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === '') {
            setFilteredUserData(allUsers.current)
        } else {
            const query = event.target.value.toLowerCase();
            setFilteredUserData(allUsers.current.filter(item =>
                item.firstName.toLowerCase().includes(query)
            ));
        }
    }

    const getAllUsers = () => {
        const token = localStorage.getItem("access_token");

        if (props.type === 'INTERNAL')
        {
            fetch(BASE_URL + "/ew/api/v1/get_all_subscribers", {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    allUsers.current = data.data;
                    setFilteredUserData(data.data);
                })
                .catch((err) => console.error("Error fetching roles:", err));
        }
        else 
        {
            fetch(`${BASE_URL}/jwt-auth/api/v1/get_all_subscribers?token=${props.token}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            })
                .then((res) => res.json())
                .then((data) => {
                    allUsers.current = data.data;
                    setFilteredUserData(data.data);
                })
                .catch((err) => console.error("Error fetching roles:", err));
        }
        
    };

    useEffect(() => {
        getAllUsers();
    }, [])

    useEffect(() => {
        const sliced = paginationList(filteredUsersData, sliceCount);
        setSlicedData(sliced);
        setSelectedPage(0);
    }, [filteredUsersData, sliceCount]);

    // Initialize from props ONLY once - this sets the initial state from parent
    useEffect(() => {
        if (hasInitializedFromProps.current) return;
        
        // Only initialize if we have initial data from props
        if (props.userSelect.length > 0) {
            const initialCodes = new Set<string>();
            const selectedCodesFromProps = new Set<string>();
            
            props.userSelect.forEach(user => {
                if (user.sms) {
                    selectedCodesFromProps.add(`${user.id}-sms`);
                    initialCodes.add(`${user.id}-sms`);
                }
                if (user.whatsapp) {
                    selectedCodesFromProps.add(`${user.id}-whatsapp`);
                    initialCodes.add(`${user.id}-whatsapp`);
                }
                if (user.viber) {
                    selectedCodesFromProps.add(`${user.id}-viber`);
                    initialCodes.add(`${user.id}-viber`);
                }
            });

            setSelectedCodes(selectedCodesFromProps);
            setInitialSelectedCodes(initialCodes);
            hasInitializedFromProps.current = true;
        }
        
    }, [props.userSelect]);

    const handleToggleChannel = (event: ChangeEvent<HTMLInputElement>) => {
        const parts = event.target.name.split("-");
        const channel = parts.pop() as "sms" | "whatsapp" | "viber";
        const username = parts.join("-");
        const checked = event.target.checked;

        const channelCode = `${username}-${channel}`;
        
        // Check if this is an initial selection
        const isInitialSelection = initialSelectedCodes.has(channelCode);
        
        // Update selectedCodes state
        setSelectedCodes((prev) => {
            const updated = new Set(prev);
            if (checked) {
                updated.add(channelCode);
            } else {
                updated.delete(channelCode);
            }
            return updated;
        });

        // Update parent state - but only if it's NOT an initial selection
        // (initial selections should remain in the list even if they can't be toggled)
        props.setUserSelect((prev) => {
            // Find existing user or create new one
            const current = prev.find((u) => u.id === username) ?? {
                isEditable: false,
                id: username,
                firstName: allUsers.current.find((u) => u.id === username)?.firstName || "",
                lastName: allUsers.current.find((u) => u.id === username)?.lastName || "",
                email: allUsers.current.find((u) => u.id === username)?.email || "",
                sms: false,
                whatsapp: false,
                viber: false,
            }

            const updatedUser: UserDataRow = {
                ...current,
                [channel]: checked,
            }

            const filteredList = prev.filter((u) => u.id !== username);
            const finalList = [...filteredList, updatedUser];

            // Return users with at least one checked field OR users that are initial selections
            return finalList.filter((u) => 
                u.sms || u.whatsapp || u.viber || 
                initialSelectedCodes.has(`${u.id}-sms`) || 
                initialSelectedCodes.has(`${u.id}-whatsapp`) || 
                initialSelectedCodes.has(`${u.id}-viber`)
            );
        });
    }

    return (
        <div className='subTable-wrapper' style={{width: 'fit-content', flex: '1'}}>
            <span className='main-title'>Select Subscribers</span>
            <div className='field-wrapper'>
                <div className='left'>
                    <span>show <input type="number" ref={rowNumber} value={sliceCount} min={1} onChange={(e) => handleSliceCountChange(e)} /> entries</span>
                </div>
                <div className='right'>
                    <span> search <input type="text" ref={searchText} onChange={(e) => handleSearchTextChange(e)} placeholder='Search here' /></span>
                </div>
            </div>
            <div className='table-body'>
                <div className='table-wrapper'>
                    <div className='table-row header' style={{ gridTemplateColumns: `1fr 4.0rem 5.5rem 4.0rem` }}>
                        <span>Name</span>
                        <span>SMS</span>
                        <span>Whatsapp</span>
                        <span>Viber</span>
                    </div>
                </div>
                {slicedData[selectedPage] && slicedData[selectedPage].map((row, index) => (
                    <div key={index} className='table-wrapper'>
                        <div className='table-row' style={{ gridTemplateColumns: `1fr 4.0rem 5.5rem 4.0rem` }}>
                            <span>{row.firstName}</span>
                            <span style={{ justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    name={`${row.id}-sms`}
                                    onChange={(e) => handleToggleChannel(e)}
                                    checked={selectedCodes.has(`${row.id}-sms`)}
                                    disabled={initialSelectedCodes.has(`${row.id}-sms`)}
                                />
                            </span>
                            <span style={{ justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    name={`${row.id}-whatsapp`}
                                    onChange={(e) => handleToggleChannel(e)}
                                    checked={selectedCodes.has(`${row.id}-whatsapp`)}
                                    disabled={initialSelectedCodes.has(`${row.id}-whatsapp`)}
                                />
                            </span>
                            <span style={{ justifyContent: 'center' }}>
                                <input
                                    type="checkbox"
                                    name={`${row.id}-viber`}
                                    onChange={(e) => handleToggleChannel(e)}
                                    checked={selectedCodes.has(`${row.id}-viber`)}
                                    disabled={initialSelectedCodes.has(`${row.id}-viber`)}
                                />
                            </span>
                        </div>
                    </div>
                ))}
            </div>
            
            {filteredUsersData.length === 0 && (
                <div className='table-no-data-wrapper'>
                    <span>no any data available</span>
                </div>
            )}
            <div className='table-pagination-wrapper'>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("prev")}>&lt;</div>
                <div className='page-wrapper'>
                    {slicedData.map((page, index) => (
                        <span key={index} className={selectedPage === index ? 'icon-page-box selected' : 'icon-page-box'} onClick={(e) => setSelectedPage(index)}>{index + 1}</span>
                    ))}
                </div>
                <div className='icon-page-box' style={{ marginBottom: "0.5rem" }} onClick={(e) => paginationNavigation("next")}>&gt;</div>
            </div>
        </div>
    )
}

export default UserSelectTable;